'use client';

import { useTranslations } from 'next-intl';
import CustomLink from 'components/shared/CustomLink';
import { CustomImage } from 'components/shared/custom-image/CustomImage';
import { ringProUsAsset } from '../ringProUsAsset';
import styles from './RingProUsPay.module.css';

const bnplLogos = ringProUsAsset('pay/bnpl-logos.png');
const heroesRing = ringProUsAsset('pay/pay-heroes-card.png');
const hsaPhoto = ringProUsAsset('pay/pay-hsa.png');

/**
 * "Pay your way." (Figma 2561:12807): centred header, three 464 x 480
 * cards — HSA/FSA, buy now pay later logos, heroes — each with its
 * copy on a frosted band. The gift banner is hidden for now.
 */
export const RingProUsPay = () => {
  const t = useTranslations('homepage');
  const k = (key: string) => `homepage.ringProUs.pay.${key}`;

  const copy = (id: 'hsa' | 'payLater' | 'heroes') => (
    <span className={styles.copy}>
      <span className={styles.cardTitle}>{t(k(`${id}.title`))}</span>
      <span className={styles.cardBody}>{t(k(`${id}.body`))}</span>
      <span className={styles.btn}>{t(k(`${id}.cta`))}</span>
    </span>
  );

  return (
    <section className={styles.section} aria-labelledby="ring-pro-us-pay-title">
      <div className={styles.inner}>
        <header className={styles.head}>
          <h2 id="ring-pro-us-pay-title" className={styles.title}>
            {t(k('title'))}
          </h2>
          <p className={styles.sub}>{t(k('sub'))}</p>
        </header>

        <div className={styles.rail}>
          <ul className={styles.grid}>
            <li className={styles.cell}>
              <CustomLink
                href="/hsa-fsa"
                className={`${styles.card} ${styles.cardHsa}`}
                data-buttontype="ring_pro_us_pay_hsa"
              >
                <span className={styles.hsaMedia} aria-hidden>
                  <CustomImage
                    className={styles.hsaImg}
                    src={hsaPhoto}
                    alt=""
                    fill
                    sizes="(min-width: 992px) 464px, 100vw"
                  />
                </span>
                <span
                  className={`${styles.band} ${styles.bandHsa}`}
                  aria-hidden
                />
                {copy('hsa')}
              </CustomLink>
            </li>

            <li className={styles.cell}>
              <CustomLink
                href="/ring-pro/buy"
                className={styles.card}
                data-buttontype="ring_pro_us_pay_later"
              >
                <span className={styles.spheres} aria-hidden>
                  <CustomImage
                    className={styles.bnplImg}
                    src={bnplLogos}
                    alt=""
                    fill
                    sizes="(min-width: 992px) 464px, 80vw"
                  />
                </span>
                <span
                  className={`${styles.band} ${styles.bandPay}`}
                  aria-hidden
                />
                {copy('payLater')}
              </CustomLink>
            </li>

            <li className={styles.cell}>
              <CustomLink
                href="/heroes"
                className={styles.card}
                data-buttontype="ring_pro_us_pay_heroes"
              >
                <span className={styles.heroesMedia} aria-hidden>
                  <CustomImage
                    className={styles.heroesImg}
                    src={heroesRing}
                    alt=""
                    fill
                    sizes="(min-width: 992px) 464px, 100vw"
                  />
                </span>
                <span
                  className={`${styles.band} ${styles.bandHeroes}`}
                  aria-hidden
                />
                {copy('heroes')}
              </CustomLink>
            </li>
          </ul>
        </div>
      </div>
    </section>
  );
};

export default RingProUsPay;
